import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Stack,
  Grid2,
  Paper,
} from "@mui/material";
import {
  CalendarMonthOutlined,
  TaskAltOutlined,
  PeopleOutlined,
  MenuBookOutlined,
  NotificationsNoneOutlined,
  HelpOutlineOutlined,
  OnlinePredictionRounded,
} from "@mui/icons-material";
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import { useDispatch, useSelector } from "react-redux";
import { setLocation } from "../Reducers/navigation";
import { useGetStudents } from "../Hooks/useGetStudents";
import useGetAssessments from "../Hooks/useGetAssessments";
import { useGetTutorShift } from "../Hooks/useGetTutorShift";


// ─── Stat strip ───────────────────────────────────────────────────────────────

function MetricCard({ label, value, sub }) {
  return (
    <Box sx={{ bgcolor: "action.hover", borderRadius: 1, p: 1.75 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: "uppercase", letterSpacing: "0.05em", display: "block", mb: 0.5 }}
      >
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={500} lineHeight={1}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
}

// ─── Action card ──────────────────────────────────────────────────────────────

function ActionCard({ icon: Icon, iconBg, iconColor, title, description, badge, featured, onClick }) {
  return (
    <Paper
      onClick={onClick}
      variant="outlined"
      sx={{
        borderRadius: 2,
        p: 2.25,
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        border: featured ? "2px solid" : "0.5px solid",
        borderColor: featured ? "primary.main" : "divider",
        transition: "border-color 0.15s, background 0.15s",
        "&:hover": { bgcolor: "action.hover", borderColor: featured ? "primary.dark" : "text.disabled" },
        "&:active": { transform: "scale(0.99)" },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2,
          bgcolor: iconBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon sx={{ fontSize: 20, color: iconColor }} />
      </Box>

      <Box>
        <Typography variant="body2" fontWeight={500} gutterBottom={false}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.5, mt: 0.25 }}>
          {description}
        </Typography>
      </Box>

      {badge && (
        <Box
          sx={{
            display: "inline-flex",
            alignSelf: "flex-start",
            px: 1,
            py: 0.25,
            borderRadius: 10,
            bgcolor: "primary.50",
            border: "1px solid",
            borderColor: "primary.200",
          }}
        >
          <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 600, color: "primary.800" }}>
            {badge}
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

// ─── Action definitions ───────────────────────────────────────────────────────
// Add or remove items here to control what appears on the hub.

const ACTIONS = [
  {
    key: "TrackSession",
    title: "Track a session",
    description: "Log attendance, notes, and progress for today's sessions.",
    icon: CalendarMonthOutlined,
    iconBg: "#E6F1FB",
    iconColor: "#185FA5",   // Blue — primary action, keep as-is
    featured: true,
    badge: "Primary action",
  },
  {
    key: "Announcements",
    title: "Announcements",
    description: "Post updates or notes visible to your students.",
    icon: NotificationsNoneOutlined,
    iconBg: "#FAECE7",
    iconColor: "#993C1D",   // Red-orange — keep as-is
  },
  {
    key: "Assessments",
    title: "Assessments",
    description: "Create, assign, or review student assessments.",
    icon: TaskAltOutlined,
    iconBg: "#E1F5EE",
    iconColor: "#0F6E56",   // Green — keep as-is
  },
  {
    key: "Assessments sessions",
    title: "Assessment sessions",
    description: "Create, grade, delete online assessment sessions.",
    icon: OnlinePredictionRounded,  // different icon — suggests live/online
    iconBg: "#E0F7FA",
    iconColor: "#00838F",   // Cyan/teal — distinct from green Assessments
  },
  {
    key: "Sessions",
    title: "Sessions",
    description: "Log sessions, view, edit, review past sessions.",
    icon: FormatListNumberedIcon,
    iconBg: "#E8EAF6",
    iconColor: "#3949AB",   // Indigo — distinct from blue TrackSession
  },
  {
    key: "Students",
    title: "Students",
    description: "View your student list, reports, and progress.",
    icon: PeopleOutlined,
    iconBg: "#EEEDFE",
    iconColor: "#534AB7",   // Purple — keep as-is
  },
  {
    key: "Material",
    title: "Materials",
    description: "Browse curriculum and lesson resources.",
    icon: MenuBookOutlined,
    iconBg: "#FAEEDA",
    iconColor: "#854F0B",   // Amber — keep as-is
  },
  {
    key: "Help",
    title: "Help",
    description: "Guides, FAQs, and support resources.",
    icon: HelpOutlineOutlined,
    iconBg: "#F0F0F0",
    iconColor: "#6B7280",   // Neutral gray — intentionally muted
  },
];

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function TutorHome() {
    const dispatch = useDispatch();
    const role = useSelector((state) => state.role);
    const locations = useSelector((state) => state.role.tutor_locations);

    const { students, fetchStudents} = useGetStudents();
    const { assessments, fetchAssessments} = useGetAssessments();
    const { sessionCount, fetchTutorSchedule } = useGetTutorShift();

    const firstName = role?.first_name ?? "there";

    const hour = new Date().getHours();
    const greeting =
      hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

    const today = new Date().toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    function navigate(location) {
      dispatch(setLocation(location));
    }

  
    async function getStudentCount() { if (locations) { await fetchStudents(locations[0].id); } }
    async function getAssessmentCount() { await fetchAssessments()}
    async function getTutoSessionCount() { await fetchTutorSchedule() }

    useEffect(() => {
      getStudentCount();
      getAssessmentCount();
      getTutoSessionCount();
    }, [])

  return (
    <Box sx={{ px: { xs: 1, sm: 2 }, py: 2 }}>

      {/* ── Greeting ── */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={500}>
          {greeting}, {firstName}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {today}
        </Typography>
      </Box>

      {/* ── Quick stats — wire these to real data as needed ── */}
      <Grid2 container spacing={1.25} sx={{ mb: 3 }}>
        <Grid2 size={{ xs: 6, sm: 3 }}>
          <MetricCard label="Session's avail" value={sessionCount ? sessionCount: "-"} sub="Log a session to track" />
        </Grid2>
        <Grid2 size={{ xs: 6, sm: 3 }}>
          <MetricCard label="Active students" value={students ? students.length : 0} sub="This semester" />
        </Grid2>
        <Grid2 size={{ xs: 6, sm: 3 }}>
          <MetricCard label="Assessments" value={assessments ? assessments.length : 0} sub="Pending review" />
        </Grid2>
        <Grid2 size={{ xs: 6, sm: 3 }}>
          <MetricCard label="Hours logged" value="—" sub="This week" />
        </Grid2>
      </Grid2>

      {/* ── Action cards ── */}
      <Typography
        variant="caption"
        sx={{
          display: "block",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          fontWeight: 500,
          color: "text.disabled",
          mb: 1.25,
        }}
      >
        What would you like to do?
      </Typography>

      <Grid2 container spacing={1.5} sx={{ mb: 3 }}>
        {ACTIONS.map((action) => (
          <Grid2 key={action.key} size={{ xs: 12, sm: 6, md: 4 }}>
            <ActionCard
              icon={action.icon}
              iconBg={action.iconBg}
              iconColor={action.iconColor}
              title={action.title}
              description={action.description}
              badge={action.badge}
              featured={action.featured}
              onClick={() => navigate(action.key)}
            />
          </Grid2>
        ))}
      </Grid2>

    </Box>
  );
}